// src/pages/TicketsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getUser } from '../utils/auth';
import { fmtDate, statusClass, priorityClass, priorityLabel, ticketCode, timeAgo } from '../utils/formatters';
import { Circle, Clock, PauseCircle, CheckCircle, Lock, RefreshCw, ClipboardList, Check, X, Search, Archive, Inbox, FolderOpen, BookOpen, Activity, BarChart3, Filter, Plus, User, UserCircle } from 'lucide-react';

/* ── Shared Design Tokens ───────────────────────────────────── */
const C = {
  nav: 'var(--brand)',
  hover: 'var(--text-primary)',
  border: 'var(--white-07)',
  bg: 'transparent',
  ptxt: 'var(--text-primary)',
  stxt: 'var(--text-muted)',
};

const TICKET_STYLES = `
  .ticket-page-wrapper { 
    height: calc(100vh - 64px); 
    display: flex; 
    flex-direction: column; 
    overflow: hidden; 
    background: transparent; 
    position: relative;
  }
  
  .header-row { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 16px; 
  }

  .ticket-header-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
  }

  .ticket-page-wrapper .ticket-card { 
    background-color: var(--ticket-card); 
    transform: translatex(0px) translatey(0px);
    border: 1px solid var(--white-04); 
    border-radius: 14px; 
    display: flex; 
    flex-direction: column; 
    flex: 1; 
    min-height: 0; 
    margin-bottom: 24px;
    overflow: hidden;
    backdrop-filter: blur(16px);
  }

  .ticket-table-container { 
    flex: 1; 
    display: flex; 
    flex-direction: column; 
    min-height: 0; 
  }

  .ticket-card .ticket-grid-header {
    display: grid;
    grid-template-columns: 110px minmax(180px, 1.5fr) 110px 100px 175px 175px 140px;
    column-gap: 12px;
    background-color: transparent;
    border-bottom: 1px solid var(--white-04);
    padding: 0 16px;
    height: 44px;
    align-items: center;
    flex-shrink: 0;
  }

  .ticket-grid-header div {
    font-size: 11px;
    font-weight: 700;
    color: rgba(156, 163, 175, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .ticket-table-body {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    background: transparent;
    padding-bottom: 80px;
  }

  .ticket-row {
    display: grid;
    grid-template-columns: 110px minmax(180px, 1.5fr) 110px 100px 175px 175px 140px;
    column-gap: 12px;
    height: 64px;
    padding: 0 16px;
    align-items: center;
    border-bottom: 1px solid var(--white-04);
    transition: background 0.15s ease;
    cursor: pointer;
  }

  .ticket-row:hover {
    background: rgba(96, 165, 250, 0.07);
  }

  .ticket-id { font-weight: 700; color: var(--text-secondary); font-size: 12px; letter-spacing: 0.02em; }
  .ticket-title-cell { display: flex; flex-direction: column; min-width: 0; }
  .ticket-table-body .ticket-title-cell .ticket-title { font-weight: 600; color: var(--text-d1d1d1); font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 2px; }
  .ticket-sub { font-size: 12px; color: var(--text-muted); }
  
  .ticket-status-pill {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 800;
    display: inline-block;
    width: fit-content;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid currentColor;
  }

  .ticket-priority-text { font-size: 13px; color: var(--text-secondary); font-weight: 700; display: flex; align-items: center; gap: 6px; }
  
  .ticket-assignee { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text-secondary); font-weight: 500; min-width: 0; overflow: hidden; }
  .ticket-user-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .ticket-avatar { width: 30px; height: 30px; border-radius: 50%; background: rgba(96, 165, 250, 0.2); color: var(--text-60a5fa); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; border: 1px solid rgba(96, 165, 250, 0.3); }

  .ticket-activity { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
  .activity-rel { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
  .activity-abs { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  .ticket-empty { 
    flex: 1; 
    display: flex; 
    flex-direction: column; 
    justify-content: center; 
    align-items: center; 
    text-align: center; 
    padding: 40px;
    color: var(--text-muted);
  }

  .ticket-page-wrapper .ticket-card .ticket-footer { 
    height: 52px; 
    flex-shrink: 0; 
    background-color: transparent; 
    border-top: 1px solid var(--white-04); 
    padding: 0 24px; 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    z-index: 20;
    position: sticky;
    bottom: 0;
  }

  .filter-chip { 
    display: inline-flex; 
    align-items: center; 
    padding: 8px 18px; 
    border-radius: 10px; 
    font-size: 13px; 
    font-weight: 600; 
    background: var(--white-03); 
    color: var(--text-muted); 
    border: 1px solid var(--white-05); 
    cursor: pointer; 
    transition: all 0.2s ease; 
    white-space: nowrap; 
  }
  .filter-chip:hover { background: var(--white-05); border-color: rgba(255,255,255,0.2); color: var(--text-primary); }
  .filter-chip-active { background: rgba(96, 165, 250, 0.1) !important; color: var(--text-60a5fa) !important; border-color: rgba(96, 165, 250, 0.3) !important; font-weight: 800 !important; box-shadow: 0 0 15px rgba(96, 165, 250, 0.1); }
  .filter-chip:disabled { opacity: 0.4; cursor: not-allowed; }

  .search-container { display: flex; align-items: center; gap: 8px; }
  .search-btn {
    height: 36px; padding: 0 16px; background: rgba(96, 165, 250, 0.18); color: var(--text-60a5fa); border: 1px solid rgba(96, 165, 250, 0.25); border-radius: 8px;
    font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;
  }
  .search-btn:hover { background: rgba(96, 165, 250, 0.28); }

  .ticket-skeleton { 
    height: 64px; width: 100%; border-bottom: 1px solid rgba(255,255,255,0.04); padding: 0 16px; 
    display: grid; grid-template-columns: 110px minmax(180px, 1.5fr) 110px 100px 175px 175px 140px; column-gap: 12px; align-items: center; 
  }
  .skel-box { height: 12px; background: var(--white-04); border-radius: 6px; position: relative; overflow: hidden; }
  .skel-box::after {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, var(--white-05), transparent);
    animation: shimmer 1.5s infinite;
  }
  @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .spin { animation: spin 1s linear infinite; }

  .filters-row { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
  .sort-dropdown-menu { 
    background: rgba(13, 13, 20, 0.98);
    border: 1px solid var(--white-05);
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.6);
    backdrop-filter: blur(12px);
  }
  .sort-dropdown-item { color: var(--text-secondary); }
  .sort-dropdown-item:hover { background: rgba(96, 165, 250,0.1); color: var(--text-primary); border-radius: 6px; }
  .sort-dropdown-item.active { background: rgba(96, 165, 250,0.15); color: var(--text-60a5fa); border-radius: 6px; }
`;

const STATUS_PILL_STYLES = {
  'Open': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
  'In Progress': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
  'On Hold': { bg: 'rgba(245, 158, 11, 0.12)', color: '#F59E0B', border: 'rgba(245, 158, 11, 0.25)' },
  'Resolved': { bg: 'rgba(16, 185, 129, 0.12)', color: 'var(--text-34d399)', border: 'rgba(16, 185, 129, 0.25)' },
  'Closed': { bg: 'rgba(107, 114, 128, 0.1)', color: '#9CA3AF', border: 'rgba(107, 114, 128, 0.2)' },
  'Reopened': { bg: 'rgba(239, 68, 68, 0.12)', color: '#F87171', border: 'rgba(239, 68, 68, 0.25)' },
};

const PRIORITY_COLORS = {
  critical: '#F87171',
  high: '#FB923C',
  medium: 'var(--text-60a5fa)',
  low: '#6B7280',
};

function StatusBadge({ ticket }) {
  const isReopened = ticket.last_event_type === 'reopened' && ticket.status === 'Open';
  const label = isReopened ? 'Reopened' : ticket.status;
  const s = STATUS_PILL_STYLES[label] || STATUS_PILL_STYLES['Open'];
  return <span className="ticket-status-pill" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{label}</span>;
}


/* ── Employee card-based tickets view ─────────────────────────── */
function EmployeeTickets({ tickets, loading, filter, setFilter, loadTickets, ticketCounts }) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSort, setShowSort] = useState(false);
  const [selectedSort, setSelectedSort] = useState('Latest Activity');

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const sortedTickets = [...tickets].sort((a, b) => {
    if (selectedSort === 'Latest Activity') return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    if (selectedSort === 'Recently Created') return new Date(b.created_at) - new Date(a.created_at);
    if (selectedSort === 'Oldest Pending') return new Date(a.created_at) - new Date(b.created_at);
    const pMap = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    if (selectedSort === 'Priority: High to Low') return (pMap[b.priority?.toLowerCase()] || 0) - (pMap[a.priority?.toLowerCase()] || 0);
    return 0;
  });

  const totalPages = Math.ceil(sortedTickets.length / rowsPerPage);
  const pagedTickets = sortedTickets.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const empFilters = [
    { id: 'total', label: 'Total' },
    { id: 'active', label: 'Active' },
    { id: 'resolved', label: 'Fixed' },
    { id: 'closed', label: 'Closed' },
    { id: 'shared', label: 'Shared With Me' },
  ];

  return (
    <div className="ticket-page-wrapper" style={{ padding: '24px 32px 32px' }}>
      <style>{TICKET_STYLES}</style>

      {/* HEADER SECTION */}
      <div className="header-row" style={{ alignItems: 'flex-start' }}>
        <div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 8px 0', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.textDecoration = 'none'; }}
          >
            &larr; Back
          </button>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: C.ptxt }}>My Tickets</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: C.stxt }}>Track and manage your requests</p>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 22, position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <button
              className="filter-chip"
              onClick={() => setShowSort(!showSort)}
              style={{ height: 36, gap: 6, background: 'var(--surface)' }}
            >
              <Filter size={14} /> Sort: {selectedSort}
            </button>
            {showSort && (
              <div style={{ position: 'absolute', top: 44, right: 0, background: 'rgba(13,13,20,0.98)', border: '1px solid var(--white-05)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 50, minWidth: 200, backdropFilter: 'blur(12px)', padding: 4 }}>
                {['Latest Activity', 'Recently Created', 'Priority: High to Low', 'Oldest Pending'].map(s => (
                  <div
                    key={s}
                    onClick={() => { setSelectedSort(s); setShowSort(false); }}
                    style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: selectedSort === s ? 'var(--text-60a5fa)' : 'var(--text-secondary)', background: selectedSort === s ? 'rgba(96, 165, 250,0.12)' : 'transparent', borderRadius: 7 }}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={loadTickets} className="filter-chip" style={{ background: 'var(--surface)' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
          <Link to="/tickets/new" className="filter-chip" style={{ background: C.nav, color: 'var(--surface)', textDecoration: 'none' }}>+ New Ticket</Link>
        </div>
      </div>

      {/* FILTERS SECTION */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {empFilters.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`filter-chip ${filter === f.id ? 'filter-chip-active' : ''}`}
          >
            {f.label}
            {ticketCounts[f.id] !== undefined && (
              <span style={{ marginLeft: 8, opacity: 0.6 }}>{ticketCounts[f.id]}</span>
            )}
          </button>
        ))}
      </div>

      {/* TABLE SECTION */}
      <div className="ticket-card">
        <div className="ticket-table-container">
          <div className="ticket-grid-header">
            <div>ID</div>
            <div>Ticket Title</div>
            <div>Status</div>
            <div>Priority</div>
            <div>Created By</div>
            <div>Assignee</div>
            <div style={{ textAlign: 'right' }}>Activity</div>
          </div>

          <div className="ticket-table-body">
            {loading ? (
              [...Array(5)].map((_, i) => <div key={i} className="ticket-skeleton"><div className="skel-box" /><div className="skel-box" /><div className="skel-box" /><div className="skel-box" /><div className="skel-box" /><div className="skel-box" /><div className="skel-box" /></div>)
            ) : sortedTickets.length === 0 ? (
              <div className="ticket-empty">
                <ClipboardList size={48} strokeWidth={1} style={{ color: 'var(--white-06)', marginBottom: 16 }} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  {filter === 'shared' ? 'No shared tickets' : 'No tickets found'}
                </h3>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: C.stxt }}>
                  {filter === 'shared' ? 'You are not CC\'d or watching any tickets.' : 'You don\'t have any tickets in this category.'}
                </p>
              </div>
            ) : (
              pagedTickets.map((t, idx) => (
                <div key={t.id} className="ticket-row" onClick={() => navigate(`/tickets/${t.id}`)}>
                  <div className="ticket-id">#{ticketCode(t, idx + (currentPage - 1) * rowsPerPage)}</div>
                  <div className="ticket-title-cell">
                    <span className="ticket-title">{t.title}</span>
                    <span className="ticket-sub">{t.category || 'General'}</span>
                  </div>
                  <div><StatusBadge ticket={t} /></div>
                  <div className="ticket-priority-text">{priorityLabel(t.priority)}</div>
                  <div className="ticket-assignee" title={t.created_by_username || 'Unknown'}>
                    <div className="ticket-avatar">{(t.created_by_username || 'U').charAt(0).toUpperCase()}</div>
                    <span className="ticket-user-name">{t.created_by_username || 'Unknown'}</span>
                  </div>
                  <div className="ticket-assignee" title={t.assigned_to_username || 'Unassigned'}>
                    <div className="ticket-avatar">{(t.assigned_to_username || 'U').charAt(0).toUpperCase()}</div>
                    <span className="ticket-user-name">{t.assigned_to_username || 'Unassigned'}</span>
                  </div>
                  <div className="ticket-activity">
                    <span className="activity-rel">{timeAgo(t.updated_at || t.created_at)}</span>
                    <span className="activity-abs">{fmtDate(t.updated_at || t.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* FOOTER / PAGINATION */}
          <div className="ticket-footer">
            <div style={{ fontSize: 12, color: C.stxt, fontWeight: 500 }}>
              Showing {sortedTickets.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(sortedTickets.length, currentPage * rowsPerPage)} of {sortedTickets.length}
            </div>

            <div style={{ display: 'flex', gap: 4 }}>
              <button className="filter-chip" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<<"}</button>
              <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<"}</button>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: C.ptxt, minWidth: 60, justifyContent: 'center' }}>
                Page {currentPage} of {totalPages || 1}
              </div>
              <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">"}</button>
              <button className="filter-chip" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">>"}</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.stxt, fontWeight: 500 }}>
              Rows:
              <select
                value={rowsPerPage}
                onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                style={{ height: 32, padding: '0 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'var(--surface)', fontSize: 12, outline: 'none', cursor: 'pointer' }}
              >
                {[10, 20, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Admin / IT table view ──────────────────────────────────────── */
function AdminTickets({ tickets, loading, filter, setFilter, loadTickets, navigate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchError, setSearchError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setLastRefreshed(new Date()), 15000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = () => {
    const value = searchInput.trim();
    if (!value) return;

    const regex = /^BST-INT\d+$/;
    if (!regex.test(value)) {
      setSearchError("Invalid Ticket Number");
      return;
    }

    const ticketId = value.replace("BST-INT", "");
    setSearchError("");
    setSearchTerm(ticketId);
  };

  useEffect(() => {
    if (!searchInput.trim()) {
      setSearchTerm("");
      setSearchError("");
    }
  }, [searchInput]);

  const [showSort, setShowSort] = useState(false);
  const [selectedSort, setSelectedSort] = useState('Latest Activity');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const adminFilters = [
    { id: 'open', label: 'Open' },
    { id: 'inprogress', label: 'In Progress' },
    { id: 'pending', label: 'On Hold' },
    { id: 'assignedtome', label: 'My Tickets' },
    { id: 'shared', label: 'Shared With Me' },
    { id: 'high', label: 'High Priority' },
    { id: 'unassigned', label: 'Unassigned' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'closed', label: 'Closed' },
    { id: 'today', label: 'Today' },
  ];

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const timeRangeParam = params.get('timeRange') || '';
  const engineerParam = params.get('engineer') || '';

  const filteredByFilter = tickets.filter(t => {
    // 1. Status/Filter type
    if (filter === 'open') { if (t.status !== 'Open') return false; }
    else if (filter === 'inprogress') { if (t.status !== 'In Progress') return false; }
    else if (filter === 'pending') { if (t.status !== 'On Hold') return false; }
    else if (filter === 'assignedtome') { if (t.assigned_to_username !== getUser()?.username) return false; }
    else if (filter === 'high') { if ((t.priority?.toLowerCase() !== 'high' && t.priority?.toLowerCase() !== 'critical') || t.status === 'Closed' || t.status === 'Resolved') return false; }
    else if (filter === 'unassigned') { if (t.assigned_to_username) return false; }
    else if (filter === 'resolved') { if (t.status !== 'Resolved') return false; }
    else if (filter === 'closed') { if (t.status !== 'Closed') return false; }
    else if (filter === 'shared') { if (!t.is_watching) return false; }
    else if (filter === 'today') {
      const startOfDay = new Date().setHours(0, 0, 0, 0);
      const endOfDay = new Date().setHours(23, 59, 59, 999);
      const created = new Date(t.created_at).getTime();
      if (created < startOfDay || created > endOfDay) return false;
    }

    // 2. Engineer filter
    if (engineerParam && engineerParam !== 'all') {
      if (t.assigned_to !== Number(engineerParam)) return false;
    }

    // 3. Time range filter
    if (timeRangeParam && timeRangeParam !== 'all') {
      const createdTime = new Date(t.created_at).getTime();
      const now = new Date();
      if (timeRangeParam === 'today') {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        if (createdTime < startOfDay.getTime()) return false;
      } else if (timeRangeParam === 'week') {
        const startOfWeek = new Date();
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        if (createdTime < startOfWeek.getTime()) return false;
      } else if (timeRangeParam === 'month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);
        if (createdTime < startOfMonth.getTime()) return false;
      } else if (timeRangeParam === 'year') {
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        startOfYear.setHours(0, 0, 0, 0);
        if (createdTime < startOfYear.getTime()) return false;
      }
    }
    return true;
  });

  const isSearchActive = searchTerm.trim() !== "";

  const baseTickets = isSearchActive ? tickets : filteredByFilter;

  const filteredBySearch = baseTickets.filter(t => {
    if (!isSearchActive) return true;
    return t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id.toString().includes(searchTerm) ||
      (t.created_by_username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.assigned_to_username || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const sortedTickets = [...filteredBySearch].sort((a, b) => {
    if (selectedSort === 'Latest Activity') return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    if (selectedSort === 'Recently Created') return new Date(b.created_at) - new Date(a.created_at);
    if (selectedSort === 'Oldest Pending') return new Date(a.created_at) - new Date(b.created_at);
    const pMap = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    if (selectedSort === 'Priority: High to Low') return (pMap[b.priority?.toLowerCase()] || 0) - (pMap[a.priority?.toLowerCase()] || 0);
    return 0;
  });

  const totalPages = Math.ceil(sortedTickets.length / rowsPerPage);
  const pagedTickets = sortedTickets.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const getPriorityBadge = (p) => {
    const pKey = (p || 'low').toLowerCase();
    const colors = { critical: '#F87171', high: '#FB923C', medium: 'var(--text-60a5fa)', low: '#6B7280' };
    return <span className="ticket-priority-text" style={{ color: colors[pKey] || colors.low }}>{priorityLabel(p) || 'Low'}</span>;
  };
  const getStatusBadge = (t) => {
    const sStyles = {
      'Open': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
      'In Progress': { bg: 'rgba(96, 165, 250, 0.12)', color: 'var(--text-60a5fa)', border: 'rgba(96, 165, 250, 0.25)' },
      'On Hold': { bg: 'rgba(245, 158, 11, 0.12)', color: '#F59E0B', border: 'rgba(245, 158, 11, 0.25)' },
      'Resolved': { bg: 'rgba(16, 185, 129, 0.12)', color: 'var(--text-34d399)', border: 'rgba(16, 185, 129, 0.25)' },
      'Closed': { bg: 'rgba(107, 114, 128, 0.1)', color: '#9CA3AF', border: 'rgba(107, 114, 128, 0.2)' },
    };
    const s = sStyles[t.status] || sStyles['Open'];
    return <span className="ticket-status-pill" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{t.status}</span>;
  };

  return (
    <div className="ticket-page-wrapper" style={{ padding: '24px 32px 32px' }}>
      <style>{TICKET_STYLES}</style>

      <div className="ticket-table-container">
        {/* ── HEADER ───────────────────────────────────────── */}
        <div className="header-row">
          <div className="header-left">
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: C.ptxt }}>Tickets</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.stxt }}>Manage and track customer requests</p>
              <span style={{ color: C.border }}>•</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '12px', color: 'var(--text-3b82f6)', fontWeight: 600 }}>
                Live Update • updated just now
              </div>
            </div>
          </div>

          <div className="header-right ticket-header-controls">
            <div className="search-container" style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  style={{ padding: '8px 12px 8px 34px', borderRadius: 8, border: searchError ? '1px solid #EF4444' : '1px solid var(--white-05)', fontSize: 13, minWidth: 260, height: 36, outline: 'none', background: 'rgba(0,0,0,0.3)', color: 'var(--text-primary)' }}
                />
              </div>
              <button className="search-btn" onClick={handleSearch}>Search</button>
              {searchError && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', color: '#EF4444', fontSize: '11px', fontWeight: 600 }}>{searchError}</div>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <button
                className="filter-chip"
                onClick={() => setShowSort(!showSort)}
                style={{ height: 36, gap: 6 }}
              >
                <Filter size={14} /> Sort: {selectedSort}
              </button>
              {showSort && (
                <div style={{ position: 'absolute', top: 44, right: 0, background: 'rgba(13,13,20,0.98)', border: '1px solid var(--white-05)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 50, minWidth: 200, backdropFilter: 'blur(12px)', padding: 4 }}>
                  {['Latest Activity', 'Recently Created', 'Priority: High to Low', 'Oldest Pending'].map(s => (
                    <div
                      key={s}
                      onClick={() => { setSelectedSort(s); setShowSort(false); }}
                      style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: selectedSort === s ? 'var(--text-60a5fa)' : 'var(--text-secondary)', background: selectedSort === s ? 'rgba(96, 165, 250,0.12)' : 'transparent', borderRadius: 7 }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              className="filter-chip"
              onClick={() => loadTickets()}
              style={{ height: 36, width: 36, padding: 0, justifyContent: 'center' }}
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {/* ── FILTERS ──────────────────────────────────────── */}
        <div className="filters-row">
          {adminFilters.map(f => {
            const isActive = filter === f.id;
            const count = tickets.filter(t => {
              if (f.id === 'open') return t.status === 'Open';
              if (f.id === 'inprogress') return t.status === 'In Progress';
              if (f.id === 'pending') return t.status === 'On Hold';
              if (f.id === 'assignedtome') return t.assigned_to_username === getUser()?.username;
              if (f.id === 'high') return (t.priority?.toLowerCase() === 'high' || t.priority?.toLowerCase() === 'critical') && t.status !== 'Closed' && t.status !== 'Resolved';
              if (f.id === 'unassigned') return !t.assigned_to_username;
              if (f.id === 'resolved') return t.status === 'Resolved';
              if (f.id === 'closed') return t.status === 'Closed';
              if (f.id === 'shared') return t.is_watching;
              if (f.id === 'today') {
                const startOfDay = new Date().setHours(0, 0, 0, 0);
                const endOfDay = new Date().setHours(23, 59, 59, 999);
                const created = new Date(t.created_at).getTime();
                return created >= startOfDay && created <= endOfDay;
              }
              return true;
            }).length;
            return (
              <button
                key={f.id}
                onClick={() => { setFilter(f.id); setCurrentPage(1); }}
                className={isActive ? 'filter-chip filter-chip-active' : 'filter-chip'}
              >
                {f.label} <span style={{ marginLeft: 4, opacity: 0.6 }}>({count})</span>
              </button>
            );
          })}
        </div>

        {/* ── TABLE AREA ───────────────────────────────────── */}
        <div className="ticket-card">
          <div className="ticket-table-container">
            <div className="ticket-grid-header">
              <div>ID</div>
              <div>Ticket Title</div>
              <div>Status</div>
              <div>Priority</div>
              <div>Created By</div>
              <div>Assignee</div>
              <div style={{ textAlign: 'right' }}>Activity</div>
            </div>

            <div className="ticket-table-body">
              {loading ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <div key={i} className="ticket-skeleton">
                    <div className="skel-box" style={{ width: 60 }} />
                    <div className="skel-box" style={{ width: '80%' }} />
                    <div className="skel-box" style={{ width: 80 }} />
                    <div className="skel-box" style={{ width: 70 }} />
                    <div className="skel-box" style={{ width: 100 }} />
                    <div className="skel-box" style={{ width: 100 }} />
                    <div className="skel-box" style={{ width: 80, marginLeft: 'auto' }} />
                  </div>
                ))
              ) : pagedTickets.length === 0 ? (
                <div className="ticket-empty">
                  <FolderOpen size={40} style={{ color: 'var(--white-06)', marginBottom: 16 }} />
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{filter === 'shared' ? 'No shared tickets' : 'No more tickets'}</h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>{filter === 'shared' ? 'You are not CC\'d or watching any tickets.' : 'You\'re all caught up in this category'}</p>
                  {searchTerm && (
                    <button onClick={() => { setSearchTerm(''); setSearchInput(''); }} style={{ marginTop: 12, color: 'var(--brand)', border: 'none', background: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Clear Search</button>
                  )}
                </div>
              ) : (
                pagedTickets.map((t, idx) => (
                  <div key={t.id} className="ticket-row" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <div className="ticket-id">#{ticketCode(t, idx)}</div>
                    <div className="ticket-title-cell">
                      <span className="ticket-title">{t.title}</span>
                      <span className="ticket-sub">{t.category || 'Support'}</span>
                    </div>
                    <div>{getStatusBadge(t)}</div>
                    <div>{getPriorityBadge(t.priority)}</div>
                    <div className="ticket-assignee" title={t.created_by_username || 'Unknown'}>
                      <div className="ticket-avatar">{(t.created_by_username || 'U').charAt(0).toUpperCase()}</div>
                      <span className="ticket-user-name">{t.created_by_username || 'Unknown'}</span>
                    </div>
                    <div className="ticket-assignee" title={t.assigned_to_username || 'Unassigned'}>
                      <div className="ticket-avatar">{(t.assigned_to_username || 'U').charAt(0).toUpperCase()}</div>
                      <span className="ticket-user-name">{t.assigned_to_username || 'Unassigned'}</span>
                    </div>
                    <div className="ticket-activity">
                      <span className="activity-rel">{timeAgo(t.updated_at || t.created_at)}</span>
                      <span className="activity-abs">{fmtDate(t.updated_at || t.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="ticket-footer">
            <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
              Showing {Math.min(sortedTickets.length, (currentPage - 1) * rowsPerPage + 1)} to {Math.min(sortedTickets.length, currentPage * rowsPerPage)} of {sortedTickets.length}
            </div>

            <div style={{ display: 'flex', gap: 4 }}>
              <button className="filter-chip" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<<"}</button>
              <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} style={{ padding: '0 8px', height: 28 }}>{"<"}</button>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 60, justifyContent: 'center' }}>
                Page {currentPage} of {totalPages || 1}
              </div>
              <button className="filter-chip" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">"}</button>
              <button className="filter-chip" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: '0 8px', height: 28 }}>{">>"}</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
              Rows:
              <select
                value={rowsPerPage}
                onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                style={{
                  height: 36,
                  padding: '0 12px',
                  minWidth: 80,
                  border: '1px solid var(--white-05)',
                  borderRadius: 8,
                  background: 'rgba(0,0,0,0.3)',
                  color: 'var(--text-secondary)',
                  fontSize: 12,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ── Main export ──────────────────────────────────────────────── */
export default function TicketsPage({ showToast }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = getUser();
  const isEmployee = currentUser?.role === 'employee';

  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({});
  const [filter, setFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    const f = params.get('filter') || '';
    if (!f) return isEmployee ? 'active' : 'open';
    return f;
  });
  const { request, loading } = useApi();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const f = params.get('filter');
    if (f) setFilter(f);
  }, [location.search]);

  const loadTickets = useCallback(async () => {
    try {
      const filterParam = filter === 'shared' ? 'shared' : (isEmployee ? filter : 'all');
      const data = await request(`/api/tickets?filter=${filterParam}`);

      // Normalize 'closed' to 'Closed' to ensure consistency across the application
      const normalizedData = data.map(ticket => {
        if (ticket.status === 'closed') {
          return { ...ticket, status: 'Closed' };
        }
        return ticket;
      });

      setTickets(normalizedData);
    } catch (err) {
      showToast(err.message || 'Failed to load tickets', 'error');
    }
  }, [request, filter, isEmployee, showToast]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // Load counts for badges (employee only)
  useEffect(() => {
    if (!isEmployee) return;
    const fetchCounts = async () => {
      try {
        const [total, active, resolved, closed, shared] = await Promise.all([
          request('/api/tickets?filter=total'),
          request('/api/tickets?filter=active'),
          request('/api/tickets?filter=resolved'),
          request('/api/tickets?filter=closed'),
          request('/api/tickets?filter=shared'),
        ]);
        setTicketCounts({
          total: (total || []).length,
          active: (active || []).length,
          resolved: (resolved || []).length,
          closed: (closed || []).length,
          shared: (shared || []).length,
        });
      } catch { /* noop */ }
    };
    fetchCounts();
  }, [request, isEmployee]);

  if (isEmployee) {
    return (
      <EmployeeTickets
        tickets={tickets}
        loading={loading}
        filter={filter}
        setFilter={setFilter}
        loadTickets={loadTickets}
        ticketCounts={ticketCounts}
      />
    );
  }

  return (
    <AdminTickets
      tickets={tickets}
      loading={loading}
      filter={filter}
      setFilter={setFilter}
      loadTickets={loadTickets}
      navigate={navigate}
    />
  );
}
